// src/pages/machines/MachineDetail.jsx
// Full machine detail — info + complete service history
// Queries: site_machines/{id} + service_tasks where machine_ids contains id
// Multi-tenant safe: company_id on every query

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc, getDoc, collection, getDocs, query, where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { formatMachineCapacity } from "../../utils/machineCapacity";

const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");

const STATUS_CONFIG = {
  working:      { label: "Working",      bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  under_repair: { label: "Under Repair", bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  breakdown:    { label: "Breakdown",    bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  inactive:     { label: "Inactive",     bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
};

const SERVICE_STATUS = {
  open:                       { label: "Open",             color: "#64748b" },
  assigned:                   { label: "Assigned",         color: "#2563eb" },
  in_progress:                { label: "In Progress",      color: "#d97706" },
  completed_pending_approval: { label: "Pending Approval", color: "#7c3aed" },
  closed:                     { label: "Closed",           color: "#166534" },
  cancelled:                  { label: "Cancelled",        color: "#dc2626" },
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  if (typeof ts === "string") return new Date(ts).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return "—";
};

export default function MachineDetail() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const me        = getUser();
  const companyId = me.company_id || "";

  const [machine,  setMachine]  = useState(null);
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      // Load machine
      const mSnap = await getDoc(doc(db, "site_machines", id));
      if (!mSnap.exists()) { alert("Machine not found"); navigate("/machines"); return; }
      const machineData = { id: mSnap.id, ...mSnap.data() };
      if (machineData.company_id !== companyId) {
        alert("Access denied — this machine belongs to a different company");
        navigate("/machines");
        return;
      }
      setMachine(machineData);

      // Load all service tasks linked to this machine
      const sSnap = await getDocs(query(
        collection(db, "service_tasks"),
        where("company_id",  "==", companyId),
        where("machine_ids", "array-contains", id),
      ));

      const svcList = sSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));

      setServices(svcList);
    } catch (err) {
      console.error("MachineDetail loadAll error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ fontSize: "28px", marginBottom: "10px" }}>⏳</div>
      Loading machine details...
    </div>
  );

  if (!machine) return null;

  const st = STATUS_CONFIG[machine.current_status] || STATUS_CONFIG.working;

  // Warranty status
  const warrantyExpired = machine.warranty_expiry && new Date(machine.warranty_expiry) < new Date();
  const warrantyDaysLeft = machine.warranty_expiry
    ? Math.ceil((new Date(machine.warranty_expiry) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Last service
  const lastService = services[0] || null;

  return (
    <div style={{ padding: "20px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: "760px", margin: "0 auto" }}>

        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button onClick={() => navigate("/machines")}
              style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: "22px" }}>←</button>
            <div>
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>
                {machine.machine_name || "Machine Detail"}
              </h2>
              <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#64748b" }}>
                📍 {machine.site_name || "—"} · {machine.client_name || "—"}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ background: st.bg, color: st.color, padding: "6px 14px", borderRadius: "999px", fontSize: "12px", fontWeight: "700" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: st.dot, display: "inline-block", marginRight: "6px" }} />
              {st.label}
            </span>
            <button onClick={() => navigate(`/machines/edit/${id}`)}
              style={{ padding: "8px 16px", borderRadius: "10px", border: "1.5px solid #e2e8f0", background: "#fff", color: "#374151", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}>
              ✏ Edit
            </button>
          </div>
        </div>

        {/* WARRANTY BANNER */}
        {machine.warranty_expiry && (
          <div style={{
            background: warrantyExpired ? "#fef2f2" : warrantyDaysLeft <= 30 ? "#fefce8" : "#f0fdf4",
            border: `1px solid ${warrantyExpired ? "#fecaca" : warrantyDaysLeft <= 30 ? "#fde68a" : "#bbf7d0"}`,
            borderRadius: "12px", padding: "12px 16px", marginBottom: "16px",
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            <span style={{ fontSize: "18px" }}>{warrantyExpired ? "⚠️" : "🛡️"}</span>
            <div>
              <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: warrantyExpired ? "#991b1b" : "#166534" }}>
                {warrantyExpired ? "Warranty Expired" : `Warranty valid — ${warrantyDaysLeft} days remaining`}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                Expiry: {new Date(machine.warranty_expiry).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </p>
            </div>
          </div>
        )}

        {/* MACHINE INFO CARD */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", marginBottom: "16px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <SectionTitle>🏭 Machine Information</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginTop: "14px" }}>
            <InfoCard label="Type"         value={machine.machine_type         || "—"} />
            <InfoCard label="Brand"        value={machine.brand                || "—"} />
            <InfoCard label="Model"        value={machine.model_no             || "—"} />
            <InfoCard label="Serial No."   value={machine.serial_no            || "—"} mono />
            <InfoCard label="Capacity"     value={formatMachineCapacity(machine)} />
            <InfoCard label="Installed"    value={machine.installation_date ? new Date(machine.installation_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"} />
            <InfoCard label="Location"     value={machine.location_description || "—"} span />
            {machine.notes && <InfoCard label="Notes" value={machine.notes} span />}
            {machine.created_by_name && (
              <InfoCard label="Added By" value={`${machine.created_by_name} · ${fmtDate(machine.created_at)}`} span />
            )}
          </div>
        </div>

        {/* QUICK STATS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "16px" }}>
          <StatCard icon="🔧" label="Total Services" value={services.length} color="#6366f1" />
          <StatCard icon="✅" label="Last Service"   value={lastService ? fmtDate(lastService.created_at) : "None"} color="#10b981" small />
          <StatCard icon="📋" label="AMC Linked"     value={machine.amc_contract_id ? "Yes" : "No"} color={machine.amc_contract_id ? "#059669" : "#94a3b8"} />
        </div>

        {/* SERVICE HISTORY */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <SectionTitle>📜 Service History</SectionTitle>
            <span style={{ background: "#f1f5f9", color: "#64748b", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: "600" }}>
              {services.length} records
            </span>
          </div>

          {services.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>🔧</div>
              <p style={{ margin: 0, fontWeight: "500" }}>No service records yet</p>
              <p style={{ margin: "4px 0 0", fontSize: "13px" }}>Services linked to this machine will appear here</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {services.map((svc, idx) => {
                const ss = SERVICE_STATUS[svc.status] || { label: svc.status, color: "#64748b" };
                const isFirst = idx === 0;
                return (
                  <div
                    key={svc.id}
                    onClick={() => navigate(`/services/${svc.id}`)}
                    style={{
                      border: `1.5px solid ${isFirst ? "#e0e7ff" : "#f1f5f9"}`,
                      borderRadius: "12px", padding: "14px 16px", cursor: "pointer",
                      background: isFirst ? "#f5f3ff" : "#fafafa",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#ede9fe"}
                    onMouseLeave={e => e.currentTarget.style.background = isFirst ? "#f5f3ff" : "#fafafa"}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                          {isFirst && <span style={{ background: "#6366f1", color: "#fff", fontSize: "10px", padding: "2px 7px", borderRadius: "999px", marginRight: "8px", fontWeight: "700" }}>LATEST</span>}
                          {svc.complaint || "Service Job"}
                        </p>
                        <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>
                          {fmtDate(svc.created_at)} · {svc.job_category || "—"} · {svc.job_type || "—"}
                        </p>
                      </div>
                      <span style={{ color: ss.color, fontSize: "12px", fontWeight: "700", flexShrink: 0, marginLeft: "8px" }}>
                        {ss.label}
                      </span>
                    </div>

                    {/* Tech + report snippet */}
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                      {svc.assigned_techs?.length > 0 && (
                        <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                          👷 {svc.assigned_techs.map(t => t.name).join(", ")}
                        </p>
                      )}
                      {svc.service_report && (
                        <p style={{ margin: 0, fontSize: "12px", color: "#64748b", flex: 1 }}>
                          📋 {svc.service_report.length > 80 ? svc.service_report.slice(0, 80) + "..." : svc.service_report}
                        </p>
                      )}
                    </div>

                    {/* Defective parts */}
                    {svc.defective_items?.length > 0 && (
                      <div style={{ marginTop: "8px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {svc.defective_items.map((d, i) => (
                          <span key={i} style={{ background: "#fef2f2", color: "#dc2626", fontSize: "11px", padding: "2px 8px", borderRadius: "6px", fontWeight: "600" }}>
                            ⚠ {d.item_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Sub-components ──
function SectionTitle({ children }) {
  return <p style={{ margin: 0, fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{children}</p>;
}

function InfoCard({ label, value, mono, span }) {
  return (
    <div style={{
      background: "#f8fafc", borderRadius: "10px", padding: "12px 14px",
      gridColumn: span ? "1 / -1" : "auto",
    }}>
      <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>{label}</p>
      <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#0f172a", fontFamily: mono ? "monospace" : "inherit" }}>{value}</p>
    </div>
  );
}

function StatCard({ icon, label, value, color, small }) {
  return (
    <div style={{ background: "#fff", borderRadius: "14px", padding: "16px", border: "1px solid #e2e8f0", textAlign: "center" }}>
      <div style={{ fontSize: "22px", marginBottom: "6px" }}>{icon}</div>
      <p style={{ margin: 0, fontSize: small ? "13px" : "22px", fontWeight: "800", color }}>{value}</p>
      <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</p>
    </div>
  );
}
